import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircleIcon,
  EnvelopeOpenIcon,
  ExclamationTriangleIcon,
  LinkSlashIcon,
} from '@heroicons/react/24/outline';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import { slideUp } from '@/lib/motion';
import { useUnsubscribeFromMarketing, useUnsubscribeStatus } from '../../services/emailUnsubscribe';

/**
 * /email/unsubscribe?token=… — the landing page for the "Unsubscribe" link in
 * marketing emails. Public: no session needed and none consulted, so it works
 * the same signed in or out, and never sends anyone to the login page.
 *
 * Loading the page only reads (GET). Unsubscribing takes a click (POST), so
 * mail scanners that prefetch every link in an email can't unsubscribe anyone.
 * Analytics never runs here (see lib/analytics.ts): the token is in the URL.
 */

const ACCOUNT_EMAILS_NOTE =
  "You'll still get account emails (sign-in codes, invitations, billing).";

const PRIMARY_BUTTON_CLASS =
  'w-full h-12 rounded-full bg-primary-500 font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const SECONDARY_LINK_CLASS =
  'text-sm text-text-secondary hover:text-text-primary transition-colors';

/** A 4xx other than rate limiting means the link itself is bad; retrying won't help. */
const isBadLink = (error: unknown): boolean => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

type Tone = 'primary' | 'success' | 'error';

const TONE_CLASS: Record<Tone, string> = {
  primary: 'bg-primary-500/10 dark:bg-primary-500/20 text-primary-500',
  success: 'bg-success-50 dark:bg-success-900/20 text-success-600 dark:text-success-400',
  error: 'bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400',
};

const StateIcon: React.FC<{ icon: React.ElementType; tone: Tone }> = ({ icon: Icon, tone }) => (
  <div className="flex justify-center mb-6">
    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${TONE_CLASS[tone]}`}>
      <Icon className="w-8 h-8" aria-hidden="true" />
    </div>
  </div>
);

/** Heading that takes focus when it appears after an action (the button it replaced is gone). */
const Heading: React.FC<{ children: React.ReactNode; focusOnMount?: boolean }> = ({
  children,
  focusOnMount = false,
}) => {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);
  return (
    <h1
      ref={ref}
      tabIndex={-1}
      className="text-[26px] font-bold text-text-primary mb-2 outline-none"
    >
      {children}
    </h1>
  );
};

const GoToAppLink: React.FC<{ primary?: boolean }> = ({ primary = false }) => (
  <Link
    to="/"
    className={
      primary
        ? 'inline-block h-12 px-8 rounded-lg bg-primary-500 font-medium text-sm hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors leading-[48px]'
        : SECONDARY_LINK_CLASS
    }
  >
    Go to Tereiko
  </Link>
);

const InvalidLink: React.FC = () => (
  <div className="text-center">
    <StateIcon icon={LinkSlashIcon} tone="error" />
    <Heading>This unsubscribe link doesn&apos;t work</Heading>
    <p className="text-sm text-text-secondary leading-relaxed mb-6">
      It may be incomplete or out of date. Open the link straight from the email, or sign in to
      Tereiko to choose which emails you get.
    </p>
    <GoToAppLink primary />
  </div>
);

const LoadFailed: React.FC<{ onRetry: () => void; retrying: boolean }> = ({
  onRetry,
  retrying,
}) => (
  <div className="text-center">
    <StateIcon icon={ExclamationTriangleIcon} tone="error" />
    <Heading>We couldn&apos;t load this page</Heading>
    <p className="text-sm text-text-secondary leading-relaxed mb-6">
      Check your connection and try again.
    </p>
    <button type="button" onClick={onRetry} disabled={retrying} className={PRIMARY_BUTTON_CLASS}>
      {retrying ? 'Trying again...' : 'Try again'}
    </button>
  </div>
);

const Unsubscribed: React.FC<{ already: boolean; email: string }> = ({ already, email }) => (
  <div className="text-center">
    <StateIcon icon={CheckCircleIcon} tone="success" />
    {already ? (
      <>
        <Heading>You&apos;re already unsubscribed</Heading>
        <p className="text-sm text-text-secondary leading-relaxed mb-6">
          <span className="font-medium text-text-primary">{email}</span> doesn&apos;t get product
          news and tips from Tereiko. {ACCOUNT_EMAILS_NOTE}
        </p>
      </>
    ) : (
      <>
        <Heading focusOnMount>You&apos;re unsubscribed from product news and tips.</Heading>
        <p className="text-sm text-text-secondary leading-relaxed mb-6">{ACCOUNT_EMAILS_NOTE}</p>
      </>
    )}
    <GoToAppLink />
  </div>
);

const UnsubscribeFlow: React.FC<{ token: string }> = ({ token }) => {
  const status = useUnsubscribeStatus(token);
  const unsubscribe = useUnsubscribeFromMarketing(token);

  if (unsubscribe.isSuccess) {
    return <Unsubscribed already={false} email={status.data?.email_masked ?? ''} />;
  }
  if (status.isPending) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="Loading">
        <div className="spinner spinner-lg text-primary-600 dark:text-primary-350" />
      </div>
    );
  }
  if (status.isError) {
    return isBadLink(status.error) ? (
      <InvalidLink />
    ) : (
      <LoadFailed onRetry={() => status.refetch()} retrying={status.isFetching} />
    );
  }
  if (unsubscribe.isError && isBadLink(unsubscribe.error)) {
    return <InvalidLink />;
  }
  if (status.data.already_unsubscribed) {
    return <Unsubscribed already email={status.data.email_masked} />;
  }

  return (
    <div>
      <StateIcon icon={EnvelopeOpenIcon} tone="primary" />
      <div className="text-center mb-8">
        <Heading>Unsubscribe from product news and tips?</Heading>
        <p className="text-sm text-text-secondary leading-relaxed">
          Tereiko will stop sending product news and tips to{' '}
          <span className="font-medium text-text-primary">{status.data.email_masked}</span>.
        </p>
      </div>

      <div className="space-y-5">
        {unsubscribe.isError && (
          <div
            role="alert"
            className="p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm"
          >
            <p>We couldn&apos;t unsubscribe you. Please try again.</p>
          </div>
        )}
        <button
          type="button"
          onClick={() => unsubscribe.mutate()}
          disabled={unsubscribe.isPending}
          className={PRIMARY_BUTTON_CLASS}
        >
          {unsubscribe.isPending ? 'Unsubscribing...' : 'Unsubscribe'}
        </button>
      </div>

      <p className="mt-5 text-center text-xs text-text-tertiary">{ACCOUNT_EMAILS_NOTE}</p>
    </div>
  );
};

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
        {token ? <UnsubscribeFlow token={token} /> : <InvalidLink />}
      </motion.div>
    </AuthLayout>
  );
};

export default UnsubscribePage;
